/**
 * recentreTail — CENTRE THE TAIL (Allen, 2026-09-24: "a single tail's
 * baseline is centre-back"; Hamish and the lady wolf, their tails thrown to
 * one side by the illustrator). Design: docs/design/TAIL_RECENTER_DESIGN_NOTE.md.
 *
 * ONE responsibility: turn the tail's REST flesh about the vertical through
 * its root by a declared yaw, keeping its droop and curl (a rotation about
 * the root, never a straightening), blended by the tail's own ownership
 * factor so the junction ring does not move and the seam band turns part of
 * the way. The weights are not touched; the caller re-places the chain on the
 * moved flesh and rebinds, so the chain, the springs and the drive all rest
 * at the centred pose.
 *
 * THE LEGS. A brush swung toward centre-back can pass through a thigh. The
 * thighs are capsules (hip joint → knee, radius = the flesh they own); the
 * turn is walked in steps toward the declared angle and STOPS at the last
 * step no tail vertex enters a capsule — said, never silent.
 *
 * Kernel-pure: positions in (bind-world), positions out. Depended on by the
 * app's run-document (the tail loop) and its tests.
 */

import type { Point3 } from '../zones/unarmatured.js';

export interface LegCapsule {
  readonly name: string;
  readonly a: Point3;
  readonly b: Point3;
  readonly radius: number;
}

export interface RecentreTailInput {
  /** Bind-world positions, xyz interleaved (not modified). */
  readonly positions: ArrayLike<number>;
  /** vertex index → its tail ownership factor f ∈ (0, 1] (the bind's; absent = 0, does not move). */
  readonly factors: ReadonlyMap<number, number>;
  /** The chain's root — the junction ring's centroid. */
  readonly root: Point3;
  /** Signed yaw, degrees, about world +Y through the root: a direction's floor angle atan2(x, z) grows by it. */
  readonly degrees: number;
  readonly legs: readonly LegCapsule[];
}

export interface RecentreTailResult {
  /** The whole mesh's positions with the tail flesh turned. */
  readonly positions: Float32Array;
  readonly declaredDeg: number;
  /** What was applied — the declared angle, or less when a leg was in the way. */
  readonly appliedDeg: number;
  /** The leg that stopped the turn short, or null. */
  readonly stoppedBy: string | null;
  readonly verticesMoved: number;
}

/** Steps the turn is walked in, toward the declared angle. */
const STEPS = 24;

export function recentreTail(input: RecentreTailInput): RecentreTailResult {
  const { positions, factors, root, degrees, legs } = input;
  const moving = [...factors.entries()].filter(([, f]) => f > 0);
  const turned = (v: number, f: number, deg: number, out: Float32Array | null): Point3 => {
    const a = (f * deg * Math.PI) / 180;
    const c = Math.cos(a);
    const s = Math.sin(a);
    const dx = positions[v * 3]! - root.x;
    const dz = positions[v * 3 + 2]! - root.z;
    const p = { x: root.x + c * dx + s * dz, y: positions[v * 3 + 1]!, z: root.z - s * dx + c * dz };
    if (out !== null) {
      out[v * 3] = p.x;
      out[v * 3 + 1] = p.y;
      out[v * 3 + 2] = p.z;
    }
    return p;
  };
  const inside = (p: Point3): string | null => {
    for (const leg of legs) {
      const abx = leg.b.x - leg.a.x;
      const aby = leg.b.y - leg.a.y;
      const abz = leg.b.z - leg.a.z;
      const l2 = abx * abx + aby * aby + abz * abz;
      const t = l2 > 0 ? Math.min(1, Math.max(0, ((p.x - leg.a.x) * abx + (p.y - leg.a.y) * aby + (p.z - leg.a.z) * abz) / l2)) : 0;
      if (Math.hypot(p.x - leg.a.x - t * abx, p.y - leg.a.y - t * aby, p.z - leg.a.z - t * abz) < leg.radius) return leg.name;
    }
    return null;
  };
  // Flesh already inside a capsule at rest (the tail's own base against the
  // buttock) says nothing about the turn — only flesh the turn MOVES in counts.
  const alreadyIn = new Set<number>();
  for (const [v] of moving) if (inside(turned(v, 0, 0, null)) !== null) alreadyIn.add(v);
  let applied = 0;
  let stoppedBy: string | null = null;
  for (let k = 1; k <= STEPS; k++) {
    const deg = (degrees * k) / STEPS;
    let hit: string | null = null;
    for (const [v, f] of moving) {
      if (alreadyIn.has(v)) continue;
      hit = inside(turned(v, f, deg, null));
      if (hit !== null) break;
    }
    if (hit !== null) {
      stoppedBy = hit;
      break;
    }
    applied = deg;
  }
  const out = Float32Array.from(positions);
  for (const [v, f] of moving) turned(v, f, applied, out);
  return { positions: out, declaredDeg: degrees, appliedDeg: applied, stoppedBy, verticesMoved: moving.length };
}

/**
 * The yaw that centres a tail: the angle, about world +Y, from its PROXIMAL
 * direction (root → a point a third of the way down it), projected on the
 * floor, to straight back (−forward). Returned as the turn to apply
 * (a {@link recentreTail} `degrees`); 0 when the direction is vertical.
 */
export function tailCentringYaw(root: Point3, proximal: Point3, forward: Point3): number {
  const dx = proximal.x - root.x;
  const dz = proximal.z - root.z;
  if (Math.hypot(dx, dz) < 1e-9) return 0;
  // Angle of the direction and of "back" in the floor plane; the turn maps one onto the other.
  const angDir = Math.atan2(dx, dz);
  const angBack = Math.atan2(-forward.x, -forward.z);
  let d = angBack - angDir;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  // recentreTail's rotation takes atan2(x, z) to atan2(x, z) + a (c·dx + s·dz, −s·dx + c·dz).
  return (d * 180) / Math.PI;
}

/**
 * The declared turn for a PLACED chain: from its root to the joint a third of
 * the way down it — the direction the chain, and so the springs, will rest
 * along. `jointsWorld` root → tip; `forward` the rig's measured facing.
 */
export function chainCentringYaw(jointsWorld: readonly Point3[], forward: Point3): number {
  if (jointsWorld.length < 2) return 0;
  const k = Math.max(1, Math.round((jointsWorld.length - 1) / 3));
  return tailCentringYaw(jointsWorld[0]!, jointsWorld[k]!, forward);
}
